import { cacheResultTypes, cacheStatusTypes } from '../actions/CacheStatusActions';
import deliveringActions from '../actions/DeliveringActions';
import timeReader from '../store/readHelpers/TimeReader';

import sessionReader from '../store/readHelpers/SessionReader';
import parentTestService from './ParentTestService';
import preCheckService from './PreCheckService';
import testStatusConstants from '../constants/TestStatusConstants';
import readinessCheckService from './ReadinessCheckService';
import multimediaService from './MultimediaService';
import storeManager from '../store/StoreManager';
import parentTestActions from '../actions/ParentTestActions';
import utilService from './UtilService';

const testComponentService = {
    assessmentHasEmbeddedOrLinkMedia(assessment = {}) {
        if (!assessment || typeof assessment !== 'object') {
            return false;
        }

        const { sections } = assessment;

        if (
            !sections ||
            typeof sections !== 'object' ||
            !sections.length ||
            sections.length === 0
        ) {
            return false;
        }

        const questions = sections.flatMap(s => s.questions);

        const containsEmbeddedYoutubeLink = questions.filter(
            q =>
                q.itemBody.includes('object class="embedCode"') &&
                q.itemBody.includes('src="https://www.youtube.com/embed/')
        );

        return containsEmbeddedYoutubeLink.length > 0;
    },

    testCanBeStarted(cacheStatus, test, shouldCacheResourcesBeforeTestStart) {
        return !!((!test.requiresDescriptionDocumentApproval || test.descriptionDocumentApproved)
            && (
                (cacheStatus.status === cacheStatusTypes.DONE && cacheStatus.result === cacheResultTypes.COMPLETE)
                || !shouldCacheResourcesBeforeTestStart
            ))
            && !this.shouldShowLoadProgress(test);
    },

    testResourcesIncompletelyCached(cacheStatus, shouldCacheResourcesBeforeTestStart) {
        return shouldCacheResourcesBeforeTestStart
            && (
                cacheStatus.status === cacheStatusTypes.DONE
                && [cacheResultTypes.ERROR, cacheResultTypes.INCOMPLETE, cacheResultTypes.TIMEOUT].includes(cacheStatus.result)
            );
    },

    shouldShowLoadProgress(cacheStatus, test, shouldCacheResourcesBeforeTestStart) {
        return shouldCacheResourcesBeforeTestStart
            && cacheStatus.status !== cacheStatusTypes.DONE
            && (!test.requiresDescriptionDocumentApproval || test.descriptionDocumentApproved);
    },

    shouldRenderChildTestActions(test, parentTest, offlineFileDeliveryStatus) {
        if (timeReader.timeHasExpired()) {
            return false;
        }
        if (this.isPendingFileDeliveryUpload((offlineFileDeliveryStatus[test.assessmentRunId] || {}).status)) {
            return false;
        }
        if (parentTestService.isChildTestCompleted(test, offlineFileDeliveryStatus)) {
            return false;
        }
        if (test.isReadinessCheck || readinessCheckService.isReadinessCheckEnabledButIncomplete()) {
            return false;
        }
        const currentTestIndex = parentTest.childTests.findIndex((childTest, index) => {
            return test.assessmentRunId === parentTest.childTests[index].assessmentRunId;
        });
        const previousTest = parentTest.childTests[currentTestIndex - 1];
        if (currentTestIndex === 0) {
            return true;
        } else if (previousTest) {
            return parentTestService.isChildTestCompleted(previousTest, offlineFileDeliveryStatus);
        }
        return false;
    },

    shouldRenderTestActions(
        test,
        parentTest,
        offlineFileDeliveryStatus,
    ) {
        return preCheckService.allShownChecksCompleted() &&
            (!sessionReader.isParentTest() || this.shouldRenderChildTestActions(test, parentTest, offlineFileDeliveryStatus));
    },

    getTestWithStatus(test) {
        const { startTimeForStudentInMillis } = test;
        // In early-entrance, Tests are deemed executing even when the effective Test startTime has not arrived yet.
        if (startTimeForStudentInMillis && sessionReader.isTestThatRequiresPreStartStepAndSittingHasNotYetStarted()) {
            const currentTime = utilService.getCurrentDate().getTime();
            if (currentTime < startTimeForStudentInMillis) {
                return { ...test, status: testStatusConstants.SCHEDULED };
            }
        }

        if (test.status) {
            return test;
        }

        return { ...test, status: testStatusConstants.EXECUTING };
    },

    isPendingFileDeliveryUpload(fileDeliveryStatus) {
        return !!fileDeliveryStatus && fileDeliveryStatus !== deliveringActions.statuses.COMPLETE;
    },

    /**
     * Checks that test description has at least one media that has been started playing
     */
    hasAtLeastOnePlayedMedia(test) {
        const mediaModels = test?.documentModel?.mediaModels || [];
        return mediaModels.some(mediaModel => multimediaService.isMediaPlayed(mediaModel));
    },

    updateHasCompletelyPlayedMedia(test) {
        const hasPlayed = this.hasAtLeastOnePlayedMedia(test);
        storeManager.dispatch(parentTestActions.setChildTestHasCompletelyPlayedDescriptionMedia(test.assessmentRunId, hasPlayed));
    },
};

export default testComponentService;
